import { useCallback, useRef, useState } from 'react';

interface Options {
    delay?: number;
    shouldPreventDefault?: boolean;
}

export function useLongPress(
    onLongPress: (e: any) => void,
    onClick?: (e: any) => void,
    { delay = 500, shouldPreventDefault = true }: Options = {}
) {
    const [longPressTriggered, setLongPressTriggered] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();
    const targetRef = useRef<EventTarget>();

    const start = useCallback(
        (event: React.MouseEvent | React.TouchEvent) => {
            if (shouldPreventDefault && event.currentTarget) {
                event.currentTarget.addEventListener('contextmenu', preventDefault, {
                    passive: false
                });
            }
            targetRef.current = event.currentTarget;
            timerRef.current = setTimeout(() => {
                onLongPress(event);
                setLongPressTriggered(true);
            }, delay);
        },
        [onLongPress, delay, shouldPreventDefault]
    );

    const clear = useCallback(
        (event: React.MouseEvent | React.TouchEvent, shouldTriggerClick = true) => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            if (shouldTriggerClick && !longPressTriggered && onClick) {
                onClick(event);
            }

            setLongPressTriggered(false);

            if (shouldPreventDefault && targetRef.current) {
                targetRef.current.removeEventListener('contextmenu', preventDefault);
            }
        },
        [onClick, longPressTriggered, shouldPreventDefault]
    );

    return {
        onPointerDown: (e: React.PointerEvent) => {
            // Only handle primary pointer (usually left click or touch)
            if (e.button !== 0) return;
            start(e);
        },
        onPointerUp: (e: React.PointerEvent) => {
            if (e.button !== 0) return;
            clear(e);
        },
        onPointerLeave: (e: React.PointerEvent) => {
            if (e.button !== 0) return;
            clear(e, false);
        },
        onContextMenu: (e: React.MouseEvent) => {
            // Prevent default if it's a long press triggered context menu
            if (longPressTriggered) {
                e.preventDefault();
            }
        }
    };
}

function preventDefault(e: Event) {
    if (!('target' in e)) return;
    e.preventDefault();
}
